import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ShieldCheck, LogOut } from "lucide-react";

const errMsg = (e, fallback) => (typeof e?.response?.data?.detail === "string" ? e.response.data.detail : fallback);

export const SecurityDialog = ({ open, onOpenChange }) => {
  const { changePassword, logoutAll } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ current: "", next: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await changePassword(form.current, form.next);
      toast.success("Password changed. All other devices were signed out.");
      setForm({ current: "", next: "" });
      onOpenChange(false);
    } catch (err) {
      toast.error(errMsg(err, "Could not change password"));
    } finally {
      setBusy(false);
    }
  };

  const signOutEverywhere = async () => {
    setBusy(true);
    try {
      await logoutAll();
      toast.success("Signed out of all devices");
    } catch {
      toast.error("Could not reach the server; signed out on this device");
    } finally {
      setBusy(false);
      navigate("/login");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-900 border-slate-800 text-white" data-testid="security-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-orange-400" /> Account security</DialogTitle>
          <DialogDescription className="text-slate-400">Changing your password signs out every other device.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label htmlFor="cur-pw">Current password</Label>
            <Input id="cur-pw" type="password" required value={form.current} onChange={(e) => setForm({ ...form, current: e.target.value })} className="bg-slate-950 border-slate-700 mt-1" data-testid="security-current-password" />
          </div>
          <div>
            <Label htmlFor="new-pw">New password (min 8 characters)</Label>
            <Input id="new-pw" type="password" required minLength={8} value={form.next} onChange={(e) => setForm({ ...form, next: e.target.value })} className="bg-slate-950 border-slate-700 mt-1" data-testid="security-new-password" />
          </div>
          <Button type="submit" disabled={busy} className="w-full bg-orange-500 hover:bg-orange-600 text-slate-900 font-semibold" data-testid="security-change-password-btn">Change password</Button>
        </form>
        <div className="border-t border-slate-800 pt-4">
          <p className="text-sm text-slate-400 mb-2">Lost a device or logged in somewhere shared? End every session, including this one.</p>
          <Button variant="outline" disabled={busy} onClick={signOutEverywhere} className="w-full border-red-500/40 text-red-300 hover:bg-red-500/10 bg-transparent" data-testid="logout-all-devices-btn">
            <LogOut className="w-4 h-4 mr-2" /> Log out all devices
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
